import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { api, UnlockError, NOT_FOUND_MESSAGE } from '../services/api';
import { formatExpiry } from '../services/retention';
import { SecretMetadata } from '../types';

const SecretViewer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [exists, setExists] = useState<boolean | null>(null);
  const [metadata, setMetadata] = useState<SecretMetadata | null>(null);
  const [pin, setPin] = useState('');
  const [content, setContent] = useState<string | null>(null);
  const [fileData, setFileData] = useState<{ data: ArrayBuffer, filename: string, mimeType: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [s1Fragment, setS1Fragment] = useState<string | null>(null);

  useEffect(() => {
    const checkStatus = async () => {
      if (!id) return;
      
      // Extract S1 from URL fragment (everything after #)
      const fragment = location.hash.substring(1); // Remove the '#'
      
      if (!fragment) {
        setExists(false);
        setIsChecking(false);
        setError('Ungültiger Link: Verschlüsselungsparameter fehlen');
        return;
      }
      
      setS1Fragment(fragment);
      
      // Check if secret exists (metadata only, no PIN needed). This never
      // consumes a one-time secret: only the PIN form below does.
      try {
        const metadata = await api.getSecretMetadata(id);
        setMetadata(metadata);
        setExists(metadata.exists);
      } catch (err) {
        setExists(false);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkStatus();
  }, [id, location.hash]);

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !pin || !s1Fragment) return;

    setIsLoading(true);
    setError(null);

    try {
      // Step 1: Verify PIN and get encrypted secret from server
      const encrypted = await api.verifyAndGetSecret(id, pin, s1Fragment);
      
      // Step 2: Decrypt the secret using PIN and S1
      const decryptedContent = await api.decryptSecret(encrypted, pin, s1Fragment);
      
      if (decryptedContent.type === 'text') {
        setContent(decryptedContent.text!);
      } else {
        setFileData({
          data: decryptedContent.content!,
          filename: decryptedContent.metadata!.filename,
          mimeType: decryptedContent.metadata!.mimeType
        });
      }
    } catch (err: any) {
      const { deleted, remainingAttempts } = err as UnlockError;
      if (deleted) {
        setExists(false);
        setError(err.message);
      } else if (remainingAttempts !== undefined && remainingAttempts <= 2) {
        setError(`Falscher PIN! Noch ${remainingAttempts} ${remainingAttempts === 1 ? 'Versuch' : 'Versuche'}, danach wird das Geheimnis gelöscht.`);
      } else {
        setError(err.message || 'Falscher PIN!');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isChecking) {
    return (
      <div className="flex flex-col items-center justify-center p-12">
        <svg className="animate-spin h-10 w-10 text-indigo-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p className="text-slate-500">Suche nach Ihrem Geheimnis...</p>
      </div>
    );
  }

  if (exists === false) {
    return (
      <div className="glass-panel p-8 rounded-2xl shadow-2xl text-center">
        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4 text-red-500">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-2xl font-semibold text-white">Geheimnis nicht gefunden.</h2>
        <p className="text-slate-400 mt-2 mb-8">
          {error || NOT_FOUND_MESSAGE}
        </p>
        <Link to="/" className="inline-block px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-xl transition-all">
          Eigenen Link erstellen
        </Link>
      </div>
    );
  }

  if (content !== null || fileData !== null) {
    const handleDownloadFile = () => {
      if (!fileData) return;
      
      const blob = new Blob([fileData.data], { type: fileData.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileData.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoking synchronously can cancel the download in some browsers
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    };
    
    return (
      <div className="glass-panel p-8 rounded-2xl shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
            </svg>
            Geheimnis entschlüsselt
          </h2>
          {content && (
            <button 
              onClick={() => {
                navigator.clipboard.writeText(content);
              }}
              className="text-xs text-indigo-400 hover:text-indigo-300 font-medium uppercase tracking-widest"
            >
              Text kopieren
            </button>
          )}
        </div>
        
        {content ? (
          <div 
            id="revealed-secret"
            className="bg-slate-900/80 border border-slate-700 rounded-xl p-6 text-indigo-100 font-mono text-sm whitespace-pre-wrap break-words min-h-[150px] leading-relaxed"
          >
            {content}
          </div>
        ) : (
          <div className="bg-slate-900/80 border border-slate-700 rounded-xl p-8 text-center">
            <svg className="w-16 h-16 text-indigo-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            <h3 className="text-lg font-semibold text-white mb-2">{fileData?.filename}</h3>
            <p className="text-slate-400 text-sm mb-6">
              {fileData && (fileData.data.byteLength / 1024 / 1024).toFixed(2)} MB
            </p>
            <button
              onClick={handleDownloadFile}
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-xl transition-all shadow-lg shadow-indigo-500/20"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Datei herunterladen
            </button>
          </div>
        )}
        
        <p className="mt-6 text-slate-500 text-xs text-center italic">
          {metadata?.oneTime
            ? 'Bitte notieren Sie sich diese Informationen jetzt. Das Geheimnis wurde gelöscht und kann nicht erneut angezeigt werden.'
            : 'Bitte notieren Sie sich diese Informationen. Nachdem dieser Link abgelaufen ist, können sie nicht erneut angezeigt werden.'}
        </p>
        <Link to="/" className="block mt-8 text-center text-slate-400 hover:text-white transition-colors text-sm">
          ← Zurück zu SecretShare
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleUnlock} className="glass-panel p-8 rounded-2xl shadow-2xl">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-indigo-600/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 className="text-2xl font-semibold text-white">Geheimnis entschlüsseln</h2>
        <p className="text-slate-400 mt-2">Geben Sie die PIN ein, um das Geheimnis anzuzeigen.</p>
        {metadata?.oneTime && (
          <p className="text-amber-200 text-sm mt-4">
            Das Geheimnis kann nur einmal angezeigt werden und wird danach gelöscht.
          </p>
        )}
        {metadata?.expiresAt && (
          <p className="text-slate-500 text-xs mt-2">Gültig bis {formatExpiry(metadata.expiresAt)}</p>
        )}
      </div>
      <div className="space-y-4">
        <div className="space-y-2">
          <input
            autoFocus
            type="password"
            required
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="PIN eingeben"
            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-4 text-center text-2xl tracking-[1em] text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          />
        </div>
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm text-center">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={isLoading || !pin}
          className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-600/50 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-xl shadow-lg shadow-indigo-500/20 transition-all active:scale-[0.98]"
        >
          {isLoading ? 'Entschlüsseln...' : 'Geheimnis anzeigen'}
        </button>
      </div>
    </form>
  );
};

export default SecretViewer;